import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import React, { useState } from 'react';
import { ALLOWED_PACK_UNITS_FOR_CONVERSION } from '@/utils/menu';
import { Button } from '@/components/button';
import {
  createMaxDecimalDigitsValidator,
  TextInput,
  validationFunctions,
  Validations,
} from '@/components/text-input';
import { createStyles } from '@/contexts/theme-context';
import { getCurrencySymbol, MICRO_CONVERSION } from '@/utils/money';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { LIST_MARGIN_HORIZONTAL, TYPOGRAPHY } from '@/styles';
import { MenuItemIngredient } from '@/contexts/menu-info-context';
import { PackUnit } from '@/enums';
import { Picker } from '@/components/picker';
import { inputValidations as priceInputValidations } from '@/components/order-guide/par-level-input';
import { Text, View } from 'react-native';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export interface MenuItemIngredientFormState {
  basePackUnit: PackUnit | null;
  baseUnitCount: number;
  ingredientName: string;
  ingredientPrice: string;
  packUnit: PackUnit;
  variantPackUUID?: string;
}

export enum IngredientActions {
  ADD = 'add',
  EDIT = 'edit',
}

const BASE_UNIT_OPTIONS = [PackUnit.Each, PackUnit.Gallon, PackUnit.Pound];

type IngredientInput = MenuItemIngredient | ItemListOptionMetadata;

export const convertToMenuItemIngredientFormState = (
  input: IngredientInput,
): MenuItemIngredientFormState => {
  const isMenuItemIngredient = 'ingredientUUID' in input;

  return {
    basePackUnit: isMenuItemIngredient
      ? (input.basePackUnit ?? null)
      : (input.baseUnit ?? null),
    baseUnitCount: input.unitCount,
    ingredientName: isMenuItemIngredient
      ? input.ingredientName
      : input.displayName,
    ingredientPrice: isMenuItemIngredient
      ? input.costInMicros
        ? (input.costInMicros / MICRO_CONVERSION).toFixed(2)
        : ''
      : input.unitPriceInMicros
        ? (input.unitPriceInMicros / MICRO_CONVERSION).toFixed(2)
        : '',
    packUnit: input.packUnit,
    variantPackUUID: input.variantPackUUID,
  };
};

interface Props {
  action: IngredientActions;
  ingredient?: MenuItemIngredientFormState;
  initialIngredientName?: string;
  onSubmit: (formState: MenuItemIngredientFormState) => void;
  originalBaseUnit?: PackUnit;
  setIngredientName?: React.Dispatch<React.SetStateAction<string>>;
}

export const MenuItemIngredientForm: React.FC<Props> = ({
  action,
  ingredient,
  initialIngredientName,
  onSubmit,
  originalBaseUnit,
  setIngredientName,
}) => {
  const [styles] = useStyles();
  const { currencyCode } = useVariantPackInfoContext();

  const initialFormState: MenuItemIngredientFormState = {
    basePackUnit: ingredient?.basePackUnit ?? PackUnit.Each,
    baseUnitCount: (ingredient?.baseUnitCount ?? '') as number, // empty string is used to represent unset in the text input
    ingredientName: ingredient?.ingredientName ?? initialIngredientName ?? '',
    ingredientPrice: ingredient?.ingredientPrice ?? '',
    packUnit: ingredient?.packUnit ?? PackUnit.Case,
    variantPackUUID: ingredient?.variantPackUUID,
  };

  const [formState, setFormState] =
    useState<MenuItemIngredientFormState>(initialFormState);

  const [ingredientPriceDisplayValue, setIngredientPriceDisplayValue] =
    useState(initialFormState.ingredientPrice);
  const [baseUnitCountDisplayValue, setBaseUnitCountDisplayValue] = useState(
    `${initialFormState.baseUnitCount}`,
  );

  const isCustomIngredient = !ingredient?.variantPackUUID;

  const isSubmitButtonDisabled =
    (isCustomIngredient &&
      (!formState.ingredientName || !formState.ingredientPrice)) || // Disable if it's a custom ingredient and the ingredient price or name is not provided
    (formState.packUnit === PackUnit.Case &&
      formState.baseUnitCount === undefined); // Disable if any ingredient has it's pack unit set to 'Case' and the base unit count is not defined

  // Handles changes in form state for any input field by updating the form state with the new value.
  const handleFormStateChange = (
    key: keyof typeof formState,
    value: string | number,
  ) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
  };

  // Function to handle form submission, passing the current form state to the onSubmit callback.
  const handleOnPress = () => {
    onSubmit(formState);
  };

  // Validations for the base unit count input
  const baseUnitCountInputValidations = [
    (input: string) => {
      if (input === '.') {
        return true;
      }
      return (
        validationFunctions[Validations.POSITIVE](input) &&
        createMaxDecimalDigitsValidator(0, 3)(input)
      );
    },
  ];

  return (
    <View style={styles.form}>
      {/* Ingredient Name Input, only editable for custom ingredients */}
      <TextInput
        autoCorrect={false}
        containerStyle={styles.inputContainer}
        editable={isCustomIngredient}
        onChangeText={(value) =>
          setIngredientName
            ? setIngredientName(value)
            : handleFormStateChange('ingredientName', value)
        }
        style={[
          TYPOGRAPHY.listTitle,
          styles.input,
          !isCustomIngredient ? styles.disabledText : null,
        ]}
        value={formState.ingredientName}
      />
      {/* Ingredient Price Input, only editable for custom ingredients */}
      <TextInput
        containerStyle={styles.inputContainer}
        editable={isCustomIngredient}
        keyboardType="numeric"
        leftElement={
          <Text style={[TYPOGRAPHY.pBold, styles.currencyText]}>
            {getCurrencySymbol(currencyCode)}
          </Text>
        }
        onBlur={(e) => {
          const parsedValue = Number(e.nativeEvent.text);
          if (isNaN(parsedValue)) {
            return;
          }
          handleFormStateChange('ingredientPrice', parsedValue.toFixed(2));
        }}
        onChangeText={(value) => setIngredientPriceDisplayValue(value)}
        placeholder={i18n.t('price')}
        style={[
          TYPOGRAPHY.listTitle,
          styles.input,
          !isCustomIngredient ? styles.disabledText : null,
        ]}
        validators={priceInputValidations}
        value={ingredientPriceDisplayValue}
      />
      {/* Pack Unit Picker, only editable for custom ingredients */}
      <Picker
        enabled={isCustomIngredient}
        onSelect={(itemValue: string) =>
          handleFormStateChange('packUnit', itemValue)
        }
        options={Array.from(ALLOWED_PACK_UNITS_FOR_CONVERSION).map((unit) =>
          LOCALIZED_PACK_UNITS[unit].toUpperCase(),
        )}
        selectedValue={formState.packUnit}
        style={[styles.picker]}
      />
      {formState.packUnit === PackUnit.Case ? (
        <View style={styles.baseUnitRow}>
          {/* Base Unit Count Input, editable for all ingredients */}
          <TextInput
            containerStyle={[styles.inputContainer, styles.horizontalInput]}
            keyboardType="decimal-pad"
            onBlur={() => {
              if (baseUnitCountDisplayValue === '.') {
                return;
              }
              const parsedValue = Number(baseUnitCountDisplayValue);
              if (isNaN(parsedValue)) {
                setBaseUnitCountDisplayValue('');
                return;
              }
              handleFormStateChange('baseUnitCount', parsedValue);
            }}
            onChangeText={(value) => {
              // This regex allows for a sequence of digits followed by up to three decimal places.
              setBaseUnitCountDisplayValue(value);
              const regex = /^\d*\.?\d{0,3}$/;
              if (regex.test(value)) {
                const parsedValue = Number(value);
                if (!isNaN(parsedValue)) {
                  handleFormStateChange('baseUnitCount', parsedValue);
                }
              }
            }}
            placeholder={i18n.t('menu_item.pack_size_placeholder')}
            style={[TYPOGRAPHY.listTitle, styles.input]}
            validators={baseUnitCountInputValidations}
            value={baseUnitCountDisplayValue}
          />
          {/* Base Pack Unit Input, enabled if custom ingredient or base unit missing on variant pack */}
          <Picker
            enabled={!ingredient?.variantPackUUID || !originalBaseUnit}
            onSelect={(itemValue: string) =>
              handleFormStateChange('basePackUnit', itemValue)
            }
            options={BASE_UNIT_OPTIONS}
            selectedValue={formState.basePackUnit ?? ''}
            style={styles.picker}
          />
        </View>
      ) : null}

      <Button
        disabled={isSubmitButtonDisabled}
        onPress={handleOnPress}
        trackingData={{
          name: `menu_item.${action}_manual_ingredient.button`,
        }}
      >
        {action === IngredientActions.ADD
          ? i18n.t('add_ingredient')
          : i18n.t('update_ingredient')}
      </Button>
    </View>
  );
};

const INGREDIENT_INPUT_HEIGHT = 50;

const useStyles = createStyles((theme) => ({
  baseUnitRow: {
    flexDirection: 'row',
    gap: 8,
  },
  currencyText: {
    color: theme.colors.inactiveFill,
    fontSize: 16,
    lineHeight: 20,
  },
  disabledText: {
    color: theme.colors.inactiveText,
  },
  form: {
    gap: 8,
    marginBottom: 8,
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  horizontalInput: {
    flex: 1,
  },
  input: {
    lineHeight: undefined,
  },
  inputContainer: {
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 2,
    height: INGREDIENT_INPUT_HEIGHT,
  },
  picker: {
    height: INGREDIENT_INPUT_HEIGHT,
  },
}));
